import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useUnit } from "@/context/UnitContext";
import { useAuth } from "@/context/AuthContext";
import { isTempSensor, toDisplayTemp, fromDisplayTemp, roundTemp, tempSymbol } from "@/lib/units";
import { toast } from "sonner";
import { Settings as SettingsIcon, Loader2, SlidersHorizontal, User, RotateCcw, KeyRound, Copy, RefreshCw, Eye, EyeOff } from "lucide-react";

const inputCls = "w-full bg-[#F8FAF8] border border-[#DCE3DD] rounded-sm px-3 py-2 text-sm focus:ring-2 focus:ring-[#275C3F] focus:ring-offset-1 outline-none";

export default function Settings() {
  const { t } = useLang();
  const { tempUnit } = useUnit();
  const { setUser } = useAuth();
  const [data, setData] = useState(null);
  const [thresholds, setThresholds] = useState({});
  const [name, setName] = useState("");
  const [savingT, setSavingT] = useState(false);
  const [savingP, setSavingP] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const load = () => api.get("/settings").then((r) => {
    setData(r.data);
    setThresholds(Object.fromEntries(r.data.sensors.map((s) => [s.key, s.threshold])));
    setName(r.data.profile.name);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const saveThresholds = async () => {
    setSavingT(true);
    try {
      await api.put("/settings/thresholds", { thresholds });
      toast.success(t("settings.saved"));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingT(false); }
  };

  const resetDefaults = () => {
    if (!data) return;
    setThresholds(Object.fromEntries(data.sensors.map((s) => [s.key, s.default])));
  };

  const saveProfile = async () => {
    setSavingP(true);
    try {
      const { data: res } = await api.put("/settings/profile", { name });
      toast.success(t("settings.profileSaved"));
      setUser((u) => (u ? { ...u, name: res.name } : u));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSavingP(false); }
  };

  const copyText = (text) => {
    navigator.clipboard?.writeText(text);
    toast.success(t("settings.copied"));
  };

  const regenerateKey = async () => {
    setRegenerating(true);
    try {
      const { data: res } = await api.post("/settings/api-key/regenerate");
      setData((d) => ({ ...d, api_key: res.api_key }));
      setShowKey(true);
      toast.success(t("settings.regenerated"));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRegenerating(false); }
  };

  const ingestUrl = data ? `${process.env.REACT_APP_BACKEND_URL}${data.ingest_path}` : "";
  const exampleCurl = data
    ? `curl -X POST ${ingestUrl} \\\n  -H "X-API-Key: ${data.api_key}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"sensor_key":"noise","value":95}'`
    : "";
  const maskedKey = data ? (showKey ? data.api_key : `${data.api_key.slice(0, 8)}${"•".repeat(20)}`) : "";

  if (!data) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-[#275C3F]" /></div>;

  return (
    <div data-testid="settings-page" className="space-y-6 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-bold flex items-center gap-2"><SettingsIcon className="h-7 w-7 text-[#275C3F]" /> {t("settings.title")}</h1>
        <p className="text-[#4B5950] mt-1">{t("settings.subtitle")}</p>
      </div>

      <section className="bg-white border border-[#DCE3DD] rounded-sm p-6">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2"><SlidersHorizontal className="h-5 w-5 text-[#275C3F]" /> {t("settings.thresholds")}</h2>
        <p className="text-sm text-[#4B5950] mt-1 mb-4">{t("settings.thresholdsHint")}</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {data.sensors.map((s) => {
            const temp = isTempSensor(s.key);
            const unitLabel = temp ? tempSymbol(tempUnit) : s.unit;
            const defaultDisplay = temp ? roundTemp(toDisplayTemp(s.default, tempUnit)) : s.default;
            const raw = thresholds[s.key];
            const inputValue = raw === "" || raw == null ? "" : (temp ? roundTemp(toDisplayTemp(raw, tempUnit)) : raw);
            return (
              <div key={s.key}>
                <label className="text-sm font-medium flex items-center justify-between">
                  <span>{s.label} <span className="text-[#4B5950] font-normal">({unitLabel})</span></span>
                  <span className="text-xs text-[#4B5950]">{t("settings.defaultLabel")}: {defaultDisplay}</span>
                </label>
                <input
                  data-testid={`threshold-input-${s.key}`}
                  type="number" step="any" min="0"
                  className={`${inputCls} mt-1`}
                  value={inputValue}
                  onChange={(e) => {
                    const v = e.target.value;
                    const canonical = v === "" ? "" : (temp ? roundTemp(fromDisplayTemp(Number(v), tempUnit)) : Number(v));
                    setThresholds({ ...thresholds, [s.key]: canonical });
                  }}
                />
              </div>
            );
          })}
        </div>
        <div className="flex items-center gap-3 mt-5">
          <button data-testid="save-thresholds-button" onClick={saveThresholds} disabled={savingT} className="inline-flex items-center gap-2 bg-[#275C3F] hover:bg-[#1E4731] text-white px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-60">
            {savingT && <Loader2 className="h-4 w-4 animate-spin" />} {t("settings.save")}
          </button>
          <button data-testid="reset-thresholds-button" onClick={resetDefaults} className="inline-flex items-center gap-2 text-sm text-[#4B5950] hover:text-[#275C3F]">
            <RotateCcw className="h-4 w-4" /> {t("settings.reset")}
          </button>
        </div>
      </section>

      <section className="bg-white border border-[#DCE3DD] rounded-sm p-6">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2"><User className="h-5 w-5 text-[#275C3F]" /> {t("settings.profile")}</h2>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <div>
            <label className="text-sm font-medium">{t("settings.name")}</label>
            <input data-testid="profile-name-input" className={`${inputCls} mt-1`} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="text-sm font-medium">{t("settings.email")}</label>
            <input className={`${inputCls} mt-1 opacity-70`} value={data.profile.email} disabled />
          </div>
        </div>
        <p className="text-xs text-[#4B5950] mt-2">{t("settings.authMethod")}: <span className="font-medium capitalize">{data.profile.auth_provider}</span></p>
        <button data-testid="save-profile-button" onClick={saveProfile} disabled={savingP} className="mt-4 inline-flex items-center gap-2 bg-[#275C3F] hover:bg-[#1E4731] text-white px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-60">
          {savingP && <Loader2 className="h-4 w-4 animate-spin" />} {t("settings.saveProfile")}
        </button>
      </section>

      <section className="bg-white border border-[#DCE3DD] rounded-sm p-6">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2"><KeyRound className="h-5 w-5 text-[#275C3F]" /> {t("settings.integrations")}</h2>
        <p className="text-sm text-[#4B5950] mt-1 mb-4">{t("settings.apiHint")}</p>

        <label className="text-sm font-medium">{t("settings.apiKey")}</label>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <code data-testid="api-key-value" className="flex-1 min-w-[240px] bg-[#F8FAF8] border border-[#DCE3DD] rounded-sm px-3 py-2 text-sm font-mono text-[#121A14] truncate">{maskedKey}</code>
          <button data-testid="reveal-api-key-button" onClick={() => setShowKey((v) => !v)} className="inline-flex items-center gap-1.5 border border-[#DCE3DD] px-3 py-2 rounded-sm text-sm hover:bg-[#EAF0EA]">
            {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />} {showKey ? t("settings.hide") : t("settings.reveal")}
          </button>
          <button data-testid="copy-api-key-button" onClick={() => copyText(data.api_key)} className="inline-flex items-center gap-1.5 border border-[#DCE3DD] px-3 py-2 rounded-sm text-sm hover:bg-[#EAF0EA]">
            <Copy className="h-4 w-4" /> {t("settings.copy")}
          </button>
          <button data-testid="regenerate-api-key-button" onClick={regenerateKey} disabled={regenerating} className="inline-flex items-center gap-1.5 border border-[#B84A22]/40 text-[#B84A22] px-3 py-2 rounded-sm text-sm hover:bg-[#B84A22]/10 disabled:opacity-60">
            {regenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {t("settings.regenerate")}
          </button>
        </div>

        <div className="mt-5">
          <p className="text-sm font-medium">{t("settings.endpoint")}</p>
          <code data-testid="ingest-endpoint" className="block mt-1 bg-[#F8FAF8] border border-[#DCE3DD] rounded-sm px-3 py-2 text-xs font-mono text-[#2E7599] break-all">POST {ingestUrl}</code>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-medium">{t("settings.example")}</p>
            <button data-testid="copy-example-button" onClick={() => copyText(exampleCurl)} className="inline-flex items-center gap-1.5 text-xs text-[#4B5950] hover:text-[#275C3F]"><Copy className="h-3.5 w-3.5" /> {t("settings.copy")}</button>
          </div>
          <pre className="bg-[#121A14] text-[#EAF0EA] rounded-sm p-4 text-xs font-mono overflow-x-auto es-scroll whitespace-pre">{exampleCurl}</pre>
        </div>
      </section>
    </div>
  );
}
